/**
 * User roles store
 * Manages the current user's roles and provides role-based permissions
 */

import { writable, derived } from 'svelte/store';
import { userRolesApi } from './api.js';

/**
 * Store for current user's roles
 * @type {import('svelte/store').Writable<string[]>}
 */
export const userRoles = writable([]);

/**
 * Store for role loading state
 * @type {import('svelte/store').Writable<boolean>}
 */
export const rolesLoading = writable(false);

/**
 * Store for role error state
 * @type {import('svelte/store').Writable<string|null>}
 */
export const rolesError = writable(null);

/**
 * Check if user has admin role
 */
export const isAdmin = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Check if user has sales role
 */
export const isSales = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('admin')
);

/**
 * Check if user has administrative-office role
 */
export const isAdministrativeOffice = derived(
  userRoles,
  $roles => $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user has customer role
 */
export const isCustomer = derived(
  userRoles,
  $roles => $roles.includes('customer') && !$roles.includes('admin')
);

/**
 * Whether the account acts for a certified reseller.
 *
 * Mirrors `isPartnerRole` on the server, including its precedence: an internal
 * account is never treated as a partner, and an account holding BOTH the
 * customer and a partner role reads as a partner — which is the normal state of
 * a customer that has been promoted to reseller. The backend resolves scope the
 * same way round, so the menu and the data agree.
 */
export const isPartner = derived(
  userRoles,
  $roles => !$roles.includes('admin')
    && ($roles.includes('partner-admin') || $roles.includes('partner-sales'))
);

/**
 * Whether the account may act for its reseller rather than only read.
 *
 * The server additionally requires the partner's certification to be current;
 * this only decides whether it is worth showing the controls.
 */
export const isPartnerAdmin = derived(
  userRoles,
  $roles => $roles.includes('partner-admin') && !$roles.includes('admin')
);

/**
 * Check if user is an internal user (not a customer)
 */
export const isInternalUser = derived(
  userRoles,
  $roles => !$roles.includes('customer') || $roles.includes('admin')
);

/**
 * Check if user can view proposals
 * Note: customers can view their own proposals (filtered on backend)
 */
export const canViewProposals = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('administrative-office') || $roles.includes('admin') || $roles.includes('customer')
);

/**
 * Check if user can view customer list (admin only)
 */
export const canViewCustomerList = derived(
  userRoles,
  $roles => $roles.includes('admin')
    || $roles.includes('partner-admin') || $roles.includes('partner-sales')
);

/**
 * Check if user can view product list (admin only)
 */
export const canViewProductList = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Whether the account may open the read-only public catalogue (/catalogo).
 *
 * A customer, and admin so the entry can be checked from an admin account
 * too. Mirrors the server: `variants.js` admits `ROLES.CUSTOMER` alongside
 * the internal roles and scopes a customer-held session to public, active
 * variants only.
 */
export const canViewCatalog = derived(
  userRoles,
  $roles => $roles.includes('customer') || $roles.includes('admin')
);

/**
 * Check if user can create proposals
 */
export const canCreateProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('admin')
);

/**
 * Check if user can submit proposals (for approval)
 */
export const canSubmitProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('admin')
);

/**
 * Check if user can approve proposals
 */
export const canApproveProposal = derived(
  userRoles,
  $roles => $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user can reject proposals
 */
export const canRejectProposal = derived(
  userRoles,
  $roles => $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user can send proposals (directly from draft in simplified workflow)
 */
export const canSendProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user can mark proposals as customer approved
 */
export const canCustomerApproveProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user can confirm proposals
 */
export const canConfirmProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Check if user can complete proposals
 */
export const canCompleteProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('administrative-office') || $roles.includes('admin')
);

/**
 * Whether the user may revise a proposal the customer asked to change.
 *
 * The same capability as creating one, because that is what revising does: it
 * withdraws the transmitted offer and opens a new draft in its place. Status
 * alone is not authorization.
 */
export const canReviseProposal = derived(
  userRoles,
  $roles => $roles.includes('sales') || $roles.includes('admin')
);

/**
 * Check if user can cancel proposals (admin only)
 */
export const canCancelProposal = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Check if user can delete proposals (admin only)
 */
export const canDeleteProposal = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Check if user can view timesheets (admin sees full list, customer sees their summary)
 */
export const canViewTimesheets = derived(
  userRoles,
  $roles => $roles.includes('admin') || $roles.includes('customer')
);

/**
 * Check if user can view compliance reports
 * Customers see their own; internal users and admins see all (backend-enforced).
 */
export const canViewComplianceReports = derived(
  userRoles,
  $roles => $roles.includes('admin') || $roles.includes('customer') || $roles.includes('sales')
    || $roles.includes('administrative-office')
    || $roles.includes('partner-admin') || $roles.includes('partner-sales')
);

/**
 * Check if user can manage settings
 */
/**
 * Whether the account has a scope the enrollment registers can be read through.
 *
 * Everyone with a lens: internal users see the fleet, a reseller its book, a
 * customer its own machines. The server decides what each of those means; this
 * only decides whether the menu entry is worth showing.
 */
export const canViewEnrollment = derived(
  userRoles,
  $roles => $roles.includes('admin') || $roles.includes('sales')
    || $roles.includes('administrative-office') || $roles.includes('customer')
    || $roles.includes('partner-admin') || $roles.includes('partner-sales')
);

export const canManagePartners = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

export const canManageSettings = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Whether the account may apply the company's qualified electronic signature.
 *
 * Admin and nothing else — not even a store that widens to admin *or* another
 * role, which is the shape of every other capability here. The certificate is
 * an automatic qualified one: a signature it produces is legally equivalent to
 * the certificate holder's handwriting, applied with no PIN prompt and no way
 * to take it back. The backend enforces the same gate with `requireAdmin()`;
 * this store only decides whether the menu entry and buttons are worth showing.
 */
export const canSignDocuments = derived(
  userRoles,
  $roles => $roles.includes('admin')
);

/**
 * Load current user's roles from the API
 * @returns {Promise<void>}
 */
export async function loadUserRoles() {
  rolesLoading.set(true);
  rolesError.set(null);

  try {
    const response = await userRolesApi.getMyRoles();
    userRoles.set(response.roles || []);
    console.log('[Roles] Loaded user roles:', response.roles);
  } catch (error) {
    console.error('[Roles] Failed to load user roles:', error);
    rolesError.set(error.message);
    userRoles.set([]);
  } finally {
    rolesLoading.set(false);
  }
}

/**
 * Clear user roles (on logout)
 */
export function clearUserRoles() {
  userRoles.set([]);
  rolesError.set(null);
}

/**
 * Check if user has a specific role
 * @param {string} role - Role to check
 * @returns {import('svelte/store').Readable<boolean>}
 */
export function hasRole(role) {
  return derived(
    userRoles,
    $roles => $roles.includes(role) || $roles.includes('admin')
  );
}

/**
 * Check if user has any of the specified roles
 * @param {string[]} roles - Roles to check
 * @returns {import('svelte/store').Readable<boolean>}
 */
export function hasAnyRole(roles) {
  return derived(
    userRoles,
    $roles => $roles.includes('admin') || roles.some(r => $roles.includes(r))
  );
}
